import { morph, MORPH_OPTIONS } from '@theme/morph';

/**
 * A class to re-render sections using the Section Rendering API
 */
class SectionRenderer {
  /**
   * The cache of section HTML
   * @type {Map<string, string>}
   */
  #cache = new Map();

  /**
   * The abort controllers by section ID
   * @type {Map<string, AbortController>}
   */
  #abortControllersBySectionId = new Map();

  /**
   * The pending promises
   * @type {Map<string, Promise<string>>}
   */
  #pendingPromises = new Map();

  constructor() {
    window.addEventListener('load', this.#cachePageSections.bind(this));
  }

  /**
   * Renders a section
   * @param {string} sectionId - The section ID
   * @param {Object} [options] - The options
   * @param {boolean} [options.cache] - Whether to use the cache
   * @param {'hydration'|'full'} [options.mode] - Which parts of the section to morph into the DOM
   * @param {URL} [options.url] - The URL to render the section from
   * @returns {Promise<string>} The rendered section HTML
   */
  async renderSection(sectionId, options) {
    const { cache = !Shopify.designMode, mode = 'full' } = options ?? {};
    const { url } = options ?? {};
    this.#abortPendingMorph(sectionId);

    const abortController = new AbortController();
    this.#abortControllersBySectionId.set(sectionId, abortController);

    const sectionHTML = await this.getSectionHTML(sectionId, cache, url);

    const filterCollapseIsOpen = document.querySelector('.filter-collapse')?.classList.contains('show');

    if (!abortController.signal.aborted) {
      this.#abortControllersBySectionId.delete(sectionId);

      morphSection(sectionId, sectionHTML, mode);
      this.priceSliderInit();
      this.activeOption();

      if (filterCollapseIsOpen) {
        document.querySelector('.filter-collapse')?.classList.add('show');
      }
    }

    return sectionHTML;
  }

  /**
   * Aborts an existing morph for a section
   * @param {string} sectionId - The section ID
   */
  #abortPendingMorph(sectionId) {
    const existingAbortController = this.#abortControllersBySectionId.get(sectionId);
    if (existingAbortController) {
      existingAbortController.abort();
    }
  }

  /**
   * Gets the HTML for a section
   * @param {string} sectionId - The section ID
   * @param {boolean} useCache - Whether to use the cache
   * @param {URL} url - The URL to render the section for
   * @returns {Promise<string>} The rendered section HTML
   */
  async getSectionHTML(sectionId, useCache = true, url = new URL(window.location.href)) {
    const sectionUrl = buildSectionRenderingURL(sectionId, url);

    let pendingPromise = this.#pendingPromises.get(sectionUrl);
    if (pendingPromise) return pendingPromise;

    if (useCache) {
      const cachedHTML = this.#cache.get(sectionUrl);

      if (cachedHTML) return cachedHTML;
    }

    pendingPromise = fetch(sectionUrl).then((response) => {
      return response.text();
    });

    this.#pendingPromises.set(sectionUrl, pendingPromise);

    const sectionHTML = await pendingPromise;
    this.#pendingPromises.delete(sectionUrl);

    this.#cache.set(sectionUrl, sectionHTML);

    if (document.querySelector('.product-loadding__filter')) {
      document.querySelector('.product-loadding__filter').classList.add('d-none');
    }

    return sectionHTML;
  }

  /**
   * Caches the page sections
   */
  #cachePageSections() {
    for (const section of document.querySelectorAll('.shopify-section')) {
      const url = buildSectionRenderingURL(section.id);
      if (this.#cache.get(url)) return;
      if (containsShadowRoot(section)) return;

      this.#cache.set(url, section.outerHTML);
    }
  }

  priceSliderInit() {
    const priceSlider = document.querySelector('#price-slider');
    if (!priceSlider) {
      return;
    }
    if (priceSlider.noUiSlider) {
      priceSlider.noUiSlider.destroy();
    }
    if (!(new URLSearchParams(location.search)).has('filter.v.price.gte')) {
      localStorage.removeItem('price.gte');
      localStorage.removeItem('price.lte');
    }
    noUiSlider.create(priceSlider, {
        start: [
          parseFloat(localStorage.getItem('price.gte') != null ? localStorage.getItem('price.gte') : 0),
          parseFloat(localStorage.getItem('price.lte') != null ? localStorage.getItem('price.lte') : priceSlider.getAttribute('data-max')) 
        ],
        connect: true,
        range: {
            'min': 0,
            'max': parseFloat(priceSlider.getAttribute('data-max'))
        }
    });

    priceSlider.noUiSlider.on('update.one', function (values, handle, unencoded, tap, positions, noUiSlider) {
      
      let prices = {
        min: values[0].replace('.', ','),
        max: values[1].replace('.', ',')
      };

      document.querySelector('.price-slider__label__from span').textContent = `${prices.min}`;
      document.querySelector('.price-slider__label__to span').textContent = `${prices.max}`;

      document.querySelector('input[name="filter.v.price.gte"]').value = prices.min;
      document.querySelector('input[name="filter.v.price.lte"]').value = prices.max;
    });

    priceSlider.noUiSlider.on('set.one', function (values, handle, unencoded, tap, positions, noUiSlider) {

      const formData = new FormData(document.querySelector('.facets__form'));

      localStorage.setItem('price.gte', formData.get('filter.v.price.gte'));
      localStorage.setItem('price.lte', formData.get('filter.v.price.lte'));

      const facetsForm = document.querySelector('facets-form-component');
      if (facetsForm && typeof facetsForm.updateFilters === 'function' && typeof facetsForm.updateSection === 'function') {
        facetsForm.updateFilters();
        facetsForm.updateSection();
      }

    });
  }

  activeOption() {

    const query = new URLSearchParams(location.search);

    const paramsNames = [
      'filter.v.option.formats',
      'filter.v.option.taille',
      'filter.p.m.custom.categorie'
    ];

    const items = document.querySelectorAll('.filter-format__list');

    items.forEach((item) => {
      item.classList.remove('active');
    });

    let found = false;

    for (const item of items) {
      const filterName = item.dataset.filter;
      const itemValue = item.dataset.id;

      if (paramsNames.includes(filterName)) {
        const activeValues = query.getAll(filterName);

        if (activeValues.includes(itemValue)) {
          item.classList.add('active');
          found = true;
        }
      }
    }

    if (!found) {
      document.querySelector('.filter-format__list[data-id="0"]')?.classList.add('active');
    }

  }
}

const SECTION_ID_PREFIX = 'shopify-section-';

/**
 * Builds a section rendering URL
 * @param {string} sectionId - The section ID
 * @param {URL} url - The URL to render the section for
 * @returns {string} The section rendering URL
 */
function buildSectionRenderingURL(sectionId, url = new URL(window.location.href)) {
  url.searchParams.set('section_id', normalizeSectionId(sectionId));
  url.searchParams.sort();

  return url.toString();
}

/**
 * Builds a section selector
 * @param {string} sectionId - The section ID
 * @returns {string} The section selector
 */
export function buildSectionSelector(sectionId) {
  return `${SECTION_ID_PREFIX}${sectionId}`;
}

/**
 * Normalizes a section ID
 * @param {string} sectionId - The section ID
 * @returns {string} The normalized section ID
 */
export function normalizeSectionId(sectionId) {
  return sectionId.replace(new RegExp(`^${SECTION_ID_PREFIX}`), '');
}

/**
 * Checks if an element contains a shadow root
 * @param {Element} element - The element to check
 * @returns {boolean} Whether the element contains a shadow root
 */
function containsShadowRoot(element) {
  return !!element.shadowRoot || Array.from(element.children).some(containsShadowRoot);
}

/**
 * @typedef {(previousElement: HTMLElement, newElement: HTMLElement) => void} UpdateCallback
 */

/**
 * Morphs the existing section element with the new section contents
 *
 * @param {string} sectionId - The section ID
 * @param {string} html - The new markup the section should morph into
 * @param {'hydration'|'full'} [mode] - Which parts of the section to morph into the DOM. 'hydration' will only morph nodes with `data-hydration-key` attributes.
 */
export async function morphSection(sectionId, html, mode = 'full') {
  const fragment = new DOMParser().parseFromString(html, 'text/html');
  const existingElement = document.getElementById(buildSectionSelector(sectionId));
  const newElement = fragment.getElementById(buildSectionSelector(sectionId));

  if (!existingElement) {
    throw new Error(`Section ${sectionId} not found`);
  }

  if (!newElement) {
    throw new Error(`Section ${sectionId} not found in the section rendering response`);
  }

  morph(existingElement, newElement, {
    ...MORPH_OPTIONS,
    hydrationMode: mode === 'hydration',
  });
}

export const sectionRenderer = new SectionRenderer();
